import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { App } from "./App";
import { initSentryClient, Sentry } from "./lib/sentry";
import {
  markChunkLoadHandlerHealthy,
  reloadOnceOnChunkFailure,
} from "./lib/handle-chunk-load-failure";
import "./index.css";

initSentryClient();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, refetchOnWindowFocus: false },
  },
});

// App-level Sentry ErrorBoundary — catches anything the per-module boundaries
// miss, and surfaces a readable fallback instead of a blank white page.
// onError handles stale-bundle chunk-load failures by reloading; per-module
// boundaries do the same so a lazy() failure inside a route also self-heals
// before the user sees the fallback.
createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary
    onError={reloadOnceOnChunkFailure}
    fallback={
      <div style={{ padding: 32, fontFamily: "system-ui, sans-serif" }}>
        <h1>Something went wrong.</h1>
        <p>The error has been reported. Reload the page to continue.</p>
        <button onClick={() => window.location.reload()}>Reload</button>
      </div>
    }
  >
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

// We rendered past the lazy() boundaries, so the chunk-load handler did its
// job — clear the one-shot reload flag so a future stale-bundle failure gets
// its own chance to self-heal instead of bypassing straight to the fallback.
markChunkLoadHandlerHealthy();
